<script setup>
const props = defineProps({
  answers: { type: Object, required: true },
  disabled: { type: Boolean, default: false }
});

const emit = defineEmits(['edit', 'submit']);

const prettyAnswers = () => {
  const a = props.answers || {};
  const goalsArr = Array.isArray(a.goals) ? a.goals : [];

  return [
    { label: 'Goal 1 type', value: a.goal1Type || (goalsArr[0]?.name ?? 'Not provided') },
    { label: 'Goal 1 horizon', value: a.goal1Horizon || (goalsArr[0]?.horizon ?? 'Not provided') },
    { label: 'Goal 2 type', value: a.goal2Type || goalsArr[1]?.name },
    { label: 'Goal 2 horizon', value: a.goal2Horizon || goalsArr[1]?.horizon },
    { label: 'Goal 3 type', value: a.goal3Type || goalsArr[2]?.name },
    { label: 'Goal 3 horizon', value: a.goal3Horizon || goalsArr[2]?.horizon },
    { label: 'Risk tolerance', value: a.riskTolerance },
    { label: 'Allocation style', value: a.allocationStyle },
    { label: 'Exclude small-cap', value: a.excludeSmallCap ? 'Yes' : 'No' },
    { label: 'Needs 80C (ELSS)', value: a.taxStatus?.needs80C ? 'Yes' : 'No' },
    { label: 'Income slab', value: a.taxStatus?.incomeSlab },
    { label: 'Income sleeve', value: a.incomeNeed ? 'Yes' : 'No' },
    { label: 'International exposure', value: a.internationalExposure ? 'Yes' : 'No' },
    { label: 'Thematic / focused funds', value: a.thematicInvesting ? 'Yes' : 'No' },
    { label: 'Multi-asset preference', value: a.multiAssetPreference ? 'Yes' : 'No' },
    { label: 'Fixed-income sleeve', value: a.fixedIncomeNeed ? 'Yes' : 'No' },
    { label: 'Arbitrage preference', value: a.arbitragePreference ? 'Yes' : 'No' },
    { label: 'Rate outlook', value: a.rateOutlook },
    { label: 'Market cycle view', value: a.marketCycle },
    { label: 'Diversification preference', value: a.diversificationPreference }
  ];
};
</script>

<template>
  <div class="review-root">
    <h2 class="title">Review your inputs</h2>
    <p class="subtitle">Check if these answers capture your situation correctly before we generate a portfolio.</p>

    <div class="review-grid">
      <div v-for="(row, idx) in prettyAnswers()" :key="idx" class="review-row">
        <div class="q">{{ row.label }}</div>
        <div class="a">{{ row.value ?? 'Not provided' }}</div>
      </div>
    </div>

    <div class="actions">
      <button class="btn-secondary" type="button" @click="emit('edit')" :disabled="disabled">
        ← Change answers
      </button>
      <button class="btn-primary" type="button" @click="emit('submit')" :disabled="disabled">
        Get my recommendation
      </button>
    </div>
  </div>
</template>

<style scoped>
.review-root {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.title {
  margin: 0;
  font-size: 1.1rem;
}

.subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: var(--an-text-muted);
}

.review-grid {
  border-radius: 8px;
  border: 1px solid #e9ecef;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 8px 16px;
}

.review-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.q {
  font-size: 0.8rem;
  color: #868e96;
}

.a {
  font-size: 0.9rem;
  font-weight: 600;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn-primary,
.btn-secondary {
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background: var(--an-primary, #1976d2);
  color: #fff;
}

.btn-secondary {
  background: #f1f3f5;
  color: #343a40;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
